import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Stack, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, InputAdornment, Chip, Paper, Skeleton,
  Pagination, Button, Tooltip, Alert, alpha,
} from "@mui/material";
import { MdSearch, MdRefresh, MdLockOutline } from "react-icons/md";
import axios from "axios";
import { getProviderMeta } from "../../../utils/labProviders";

const BASE = "https://api.medstown.com";

const C = {
  primary: "#0c4a6e",
  success: "#16a34a",
  warning: "#d97706",
  muted:   "#64748b",
};

const rupee = (n) =>
  n == null || Number.isNaN(Number(n)) ? "—" : `₹${Math.round(Number(n)).toLocaleString("en-IN")}`;

/** Margin reads green when healthy, amber when thin, red when we'd lose money. */
const marginColor = (pct) => {
  if (pct == null) return C.muted;
  if (pct <= 0) return "#dc2626";
  if (pct < 20) return C.warning;
  return C.success;
};

const fmtWhen = (iso) => {
  if (!iso) return "never";
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  return d.toLocaleString();
};

/**
 * Redcliffe's catalog, read-only.
 *
 * Unlike Orange — whose tests and packages live in our database and whose price
 * an admin sets per item — Redcliffe's catalog is fetched live from their API
 * and never stored. There is no row here to edit: the selling price is derived
 * from their MRP, and margin is that minus what they invoice us (offer_price).
 *
 * This screen exists so support can see what is on sale and at what margin, not
 * to change it. The only lever is REDCLIFFE_DISCOUNT_PERCENT on the backend,
 * which applies to the whole catalog at once.
 */
export default function RedcliffeCatalogList() {
  const meta = getProviderMeta("redcliffe");

  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch]     = useState("");
  const [debSearch, setDebSearch] = useState("");
  const [page, setPage]         = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 0 });
  const [status, setStatus]     = useState(null);
  const [error, setError]       = useState(null);
  const limit = 25;

  useEffect(() => {
    const t = setTimeout(() => { setDebSearch(search); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const p = new URLSearchParams({ page, limit });
      if (debSearch) p.append("q", debSearch);
      const [listRes, statusRes] = await Promise.all([
        axios.get(`${BASE}/admin/lab/redcliffe/catalog?${p}`),
        axios.get(`${BASE}/admin/lab/redcliffe/status`),
      ]);
      setItems(listRes.data.items || []);
      setPagination(listRes.data.pagination || { total: 0, pages: 0 });
      setStatus(statusRes.data || null);
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          "Couldn't reach Redcliffe's catalog. Their API may be down — Orange is unaffected."
      );
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, debSearch]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const forceRefresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${BASE}/admin/lab/redcliffe/refresh`);
      await fetchItems();
    } catch {
      setError("Refresh failed — showing the last cached copy.");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Box>
      {/* Why this screen has no edit controls */}
      <Alert
        severity="info"
        icon={<MdLockOutline />}
        sx={{
          mb: 1.5,
          borderRadius: 2,
          bgcolor: alpha(meta.tone, 0.06),
          border: `1px solid ${alpha(meta.tone, 0.2)}`,
          "& .MuiAlert-icon": { color: meta.tone },
        }}
      >
        <Typography variant="caption" sx={{ fontSize: "0.78rem" }}>
          <strong>Read-only.</strong> {meta.label}'s catalog is served live from their
          API and isn't stored in Medstown's database, so there's no per-item price to
          edit here. Selling price is their MRP; margin is that minus what they invoice
          us. To change pricing across the whole catalog, set{" "}
          <code>REDCLIFFE_DISCOUNT_PERCENT</code> on the backend.
        </Typography>
      </Alert>

      {error && (
        <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 2 }}>
          <Typography variant="caption">{error}</Typography>
        </Alert>
      )}

      {/* Search + cache state */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          placeholder="Search name or code…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><MdSearch color={C.muted} /></InputAdornment>
            ),
          }}
        />
        <Stack direction="row" alignItems="center" spacing={1}>
          {status && (
            <Tooltip title={`Cached for ${Math.round((status.ttlMs || 0) / 60000)} min. Their API takes ~7s cold, so the catalog is cached rather than fetched per request.`}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem" }}>
                {status.count} items · updated {fmtWhen(status.fetchedAt)}
                {status.discountPercent ? ` · ${status.discountPercent}% off MRP` : " · sold at MRP"}
              </Typography>
            </Tooltip>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<MdRefresh />}
            onClick={forceRefresh}
            disabled={refreshing}
            sx={{ textTransform: "none", fontSize: "0.75rem", borderRadius: 1.5 }}
          >
            {refreshing ? "Refreshing…" : "Refresh"}
          </Button>
        </Stack>
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(C.primary, 0.04) }}>
              {["Code", "Name", "Type", "MRP", "Selling", "Partner cost", "Margin"].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.74rem", color: C.primary }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading &&
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 7 }).map((__, j) => (
                    <TableCell key={j}><Skeleton height={20} /></TableCell>
                  ))}
                </TableRow>
              ))}

            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {debSearch
                      ? `Nothing matching "${debSearch}".`
                      : "No items returned by Redcliffe."}
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!loading && items.map((it) => (
              <TableRow key={`${it.externalId}`} hover>
                <TableCell sx={{ fontFamily: "monospace", fontSize: "0.74rem" }}>
                  {it.externalId}
                </TableCell>
                <TableCell sx={{ fontSize: "0.78rem", maxWidth: 320 }}>
                  {it.name}
                  {it.parameterCount ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.68rem" }}>
                      {it.parameterCount} parameter{it.parameterCount === 1 ? "" : "s"}
                      {it.tatLabel ? ` · ${it.tatLabel}` : ""}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={it.isPackage ? "Package" : "Test"}
                    sx={{
                      height: 20, fontSize: "0.66rem", fontWeight: 600,
                      bgcolor: alpha(it.isPackage ? "#0e7490" : C.primary, 0.1),
                      color: it.isPackage ? "#0e7490" : C.primary,
                    }}
                  />
                </TableCell>
                <TableCell sx={{ fontSize: "0.78rem", color: C.muted, textDecoration: "line-through" }}>
                  {rupee(it.mrp)}
                </TableCell>
                <TableCell sx={{ fontSize: "0.78rem", fontWeight: 700 }}>
                  {rupee(it.sellingPrice)}
                </TableCell>
                <TableCell sx={{ fontSize: "0.78rem", color: C.muted }}>
                  {rupee(it.internalCost)}
                </TableCell>
                <TableCell>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ fontSize: "0.76rem", color: marginColor(it.marginPercent) }}
                  >
                    {rupee(it.margin)}
                    {it.marginPercent != null && (
                      <Typography component="span" variant="caption" sx={{ ml: 0.5, fontSize: "0.68rem", opacity: 0.75 }}>
                        ({it.marginPercent}%)
                      </Typography>
                    )}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination.pages > 1 && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination
            count={pagination.pages}
            page={page}
            onChange={(_, v) => setPage(v)}
            size="small"
            color="primary"
          />
        </Stack>
      )}
    </Box>
  );
}
